import { useEffect, useMemo, useState } from "react";
import { X, Plus, Trash2, Search } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { CATEGORY_LABELS, CATEGORY_ORDER } from "../lib/categories";
import { groupByCategory } from "../lib/sections";

/**
 * AdminOrderEditor
 * -----------------
 * Full pre-production edit dialog for the admin. Backed by
 * `PATCH /api/orders/{id}` (accepts items, customer, pickup_date, notes,
 * internal_notes, discount_amount, discount_label, tax_rate,
 * fulfillment_location_id). Only enabled when the order is in `submitted`
 * or `sent_to_production` (kitchen hasn't pulled yet).
 *
 * Props:
 *   orderId   — required
 *   onClose   — required
 *   onSaved   — called after successful save (parent re-fetches)
 */
export default function AdminOrderEditor({ orderId, onClose, onSaved }) {
  const [order, setOrder] = useState(null);
  const [catalog, setCatalog] = useState([]);
  const [locations, setLocations] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  // Editable state
  const [customer, setCustomer] = useState("");
  const [pickupDate, setPickupDate] = useState("");
  const [notes, setNotes] = useState("");
  const [internalNotes, setInternalNotes] = useState("");
  const [discountAmount, setDiscountAmount] = useState(0);
  const [discountLabel, setDiscountLabel] = useState("");
  const [taxRate, setTaxRate] = useState(0.0825);
  const [fulfillLoc, setFulfillLoc] = useState("");
  const [items, setItems] = useState([]); // {item_id, name, quantity, category, unit_price_override, catalog_price}
  const [showAddPicker, setShowAddPicker] = useState(false);
  const [pickerQuery, setPickerQuery] = useState("");
  const [pickerCategory, setPickerCategory] = useState("macaron");

  useEffect(() => {
    if (!orderId) return;
    setErr("");
    Promise.all([
      api.get("/orders").then((r) => (r.data || []).find((o) => o.id === orderId)),
      api.get("/inventory").then((r) => r.data),
      api.get("/locations").then((r) => r.data),
    ]).then(([o, inv, locs]) => {
      if (!o) { setErr("Order not found"); return; }
      setOrder(o);
      setCatalog(inv);
      setLocations(locs);
      setCustomer(o.customer || "");
      setPickupDate(o.pickup_date || "");
      setNotes(o.notes || "");
      setInternalNotes(o.internal_notes || "");
      setDiscountAmount(o.discount_amount || 0);
      setDiscountLabel(o.discount_label || "");
      setTaxRate(0);  // Feb 2026: tax removed from franchisee invoices — field kept on model for future re-enable.
      setFulfillLoc(o.fulfillment_location_id || "");
      // Build line-item working copy — attach catalog price for reference
      const priceByName = Object.fromEntries(inv.map((i) => [i.name, i.price]));
      setItems(
        o.items.map((it) => ({
          item_id: it.item_id,
          name: it.name,
          quantity: it.quantity,
          category: it.category,
          unit_price_override: it.unit_price_override ?? null,
          catalog_price: priceByName[it.name] ?? 0,
        })),
      );
    }).catch((e) => setErr(e.message));
  }, [orderId]);

  const totals = useMemo(() => {
    let subtotal = 0;
    for (const it of items) {
      const price = it.unit_price_override != null ? it.unit_price_override : it.catalog_price;
      subtotal += (price || 0) * (it.quantity || 0);
    }
    const disc = Math.max(0, Number(discountAmount) || 0);
    const base = Math.max(0, subtotal - disc);
    return { subtotal, disc, base, tax: 0, total: base };
  }, [items, discountAmount]);

  const updateItem = (idx, patch) => {
    setItems((arr) => arr.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
  };
  const removeItem = (idx) => setItems((arr) => arr.filter((_, i) => i !== idx));
  const addFromCatalog = (c) => {
    if (items.some((i) => i.item_id === c.id)) {
      toast.info(`${c.name} is already in this order`);
      return;
    }
    setItems((arr) => [...arr, {
      item_id: c.id, name: c.name, quantity: 1,
      category: c.category, unit_price_override: null, catalog_price: c.price,
    }]);
    setShowAddPicker(false);
    setPickerQuery("");
  };

  const pickerResults = useMemo(() => {
    const q = pickerQuery.toLowerCase().trim();
    return catalog
      .filter((c) => c.category === pickerCategory)
      .filter((c) => !q || c.name.toLowerCase().includes(q))
      .slice(0, 40);
  }, [catalog, pickerCategory, pickerQuery]);

  const save = async () => {
    setErr(""); setBusy(true);
    try {
      await api.patch(`/orders/${order.id}`, {
        customer: customer || null,
        pickup_date: pickupDate || null,
        notes,
        internal_notes: internalNotes,
        discount_amount: Number(discountAmount) || 0,
        discount_label: discountLabel || null,
        tax_rate: Number(taxRate) || 0,
        fulfillment_location_id: fulfillLoc || null,
        items: items.map((it) => ({
          item_id: it.item_id,
          name: it.name,
          quantity: Math.max(0, parseInt(it.quantity, 10) || 0),
          category: it.category,
          unit_price_override: it.unit_price_override != null
            ? Number(it.unit_price_override)
            : null,
        })).filter((it) => it.quantity > 0),
      });
      toast.success(`${order.order_no} updated`);
      onSaved?.();
      onClose();
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  if (!orderId) return null;

  return (
    <div className="fixed inset-0 z-[70] bg-foreground/40 backdrop-blur-sm flex items-center justify-center px-4"
         onClick={onClose} data-testid="admin-order-editor-backdrop">
      <div onClick={(e) => e.stopPropagation()}
           className="bg-card rounded-2xl border border-border w-full max-w-3xl max-h-[92vh] flex flex-col"
           data-testid="admin-order-editor">
        <div className="p-6 border-b border-border flex items-start justify-between gap-4">
          <div>
            <div className="label-eyebrow mb-1">Edit Order</div>
            <h2 className="font-display text-2xl font-light tracking-tight">
              {order?.order_no || "…"}
            </h2>
            <p className="text-xs text-muted-foreground mt-1">
              Editable while the kitchen hasn&apos;t started pulling. Invoices will auto-apply overrides.
            </p>
          </div>
          <button onClick={onClose} className="h-8 w-8 rounded-full hover:bg-secondary flex items-center justify-center" aria-label="Close">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-6">
          {err && <div className="text-xs text-destructive bg-destructive/5 border border-destructive/20 rounded-lg px-3 py-2">{err}</div>}

          {/* Customer + pickup + fulfillment */}
          <section className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <Field label="Customer / Destination">
              <input value={customer} onChange={(e) => setCustomer(e.target.value)}
                     data-testid="editor-customer"
                     className="h-11 w-full rounded-xl border border-input bg-card px-3 text-sm" />
            </Field>
            <Field label="Pickup Date">
              <input type="date" value={pickupDate} onChange={(e) => setPickupDate(e.target.value)}
                     data-testid="editor-pickup"
                     className="h-11 w-full rounded-xl border border-input bg-card px-3 text-sm" />
            </Field>
            <Field label="Fulfillment Location">
              <select value={fulfillLoc} onChange={(e) => setFulfillLoc(e.target.value)}
                      data-testid="editor-fulfillment"
                      className="h-11 w-full rounded-xl border border-input bg-card px-3 text-sm">
                <option value="">— select —</option>
                {locations.filter((l) => l.is_warehouse).map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
              </select>
            </Field>
          </section>

          {/* Line items */}
          <section>
            <div className="flex items-center justify-between mb-2">
              <div className="label-eyebrow">Line Items ({items.length})</div>
              <button
                onClick={() => setShowAddPicker(true)}
                data-testid="editor-add-item-btn"
                className="h-9 px-3 rounded-lg border border-dashed border-border bg-card text-xs font-medium hover:bg-secondary inline-flex items-center gap-1.5"
              >
                <Plus className="h-3.5 w-3.5" /> Add Item
              </button>
            </div>
            <div className="rounded-xl border border-border overflow-hidden">
              <div className="grid grid-cols-[1fr_70px_100px_100px_40px] gap-2 px-3 py-2 bg-secondary/40 label-eyebrow text-[10px]">
                <div>Item</div>
                <div className="text-center">Qty</div>
                <div className="text-right">Unit $</div>
                <div className="text-right">Line</div>
                <div />
              </div>
              {items.length === 0 && (
                <div className="px-3 py-6 text-center text-sm text-muted-foreground">
                  No items. Click <b>Add Item</b> to include one from the catalog.
                </div>
              )}
              {groupByCategory(items).map((g) => (
              <div key={g.cat} data-testid={`editor-section-${g.cat}`}>
                <div className="px-3 pt-3 pb-1 label-eyebrow text-[10px] text-[hsl(var(--primary))] border-t border-border">{g.label}</div>
              {g.items.map((it) => {
                const idx = it._idx;
                const price = it.unit_price_override != null ? it.unit_price_override : it.catalog_price;
                const line = (price || 0) * (it.quantity || 0);
                const isOverride = it.unit_price_override != null;
                return (
                  <div key={`${it.item_id}-${idx}`} className="grid grid-cols-[1fr_70px_100px_100px_40px] gap-2 px-3 py-2.5 items-center border-t border-border">
                    <div className="min-w-0">
                      <div className="text-sm font-medium truncate">{it.name}</div>
                      <div className="text-[10px] text-muted-foreground">
                        {CATEGORY_LABELS[it.category] || it.category}
                        {" · "}catalog ${(it.catalog_price ?? 0).toFixed(2)}
                      </div>
                    </div>
                    <input type="number" min="0" value={it.quantity}
                           onChange={(e) => updateItem(idx, { quantity: parseInt(e.target.value || "0", 10) })}
                           data-testid={`editor-qty-${idx}`}
                           className="h-9 rounded-lg border border-input bg-card px-2 text-center text-sm tabular-nums" />
                    <input type="number" min="0" step="0.01"
                           value={it.unit_price_override ?? ""}
                           placeholder={(it.catalog_price ?? 0).toFixed(2)}
                           onChange={(e) => {
                             const v = e.target.value;
                             updateItem(idx, { unit_price_override: v === "" ? null : parseFloat(v) });
                           }}
                           data-testid={`editor-price-${idx}`}
                           className={`h-9 rounded-lg border bg-card px-2 text-right text-sm tabular-nums ${isOverride ? "border-primary text-[hsl(var(--primary))]" : "border-input text-muted-foreground"}`} />
                    <div className="text-right text-sm tabular-nums font-medium">${line.toFixed(2)}</div>
                    <button onClick={() => removeItem(idx)} data-testid={`editor-remove-${idx}`}
                            className="text-muted-foreground hover:text-destructive p-1" aria-label={`Remove ${it.name}`}>
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                );
              })}
                <div className="grid grid-cols-[1fr_70px_100px_100px_40px] gap-2 px-3 py-2 bg-secondary/40 text-xs font-medium" data-testid={`editor-section-total-${g.cat}`}>
                  <div>{g.label} total</div>
                  <div className="text-center tabular-nums">{g.items.reduce((t, it) => t + (it.quantity || 0), 0)}</div>
                  <div />
                  <div className="text-right tabular-nums">${g.items.reduce((t, it) => t + ((it.unit_price_override != null ? it.unit_price_override : it.catalog_price) || 0) * (it.quantity || 0), 0).toFixed(2)}</div>
                  <div />
                </div>
              </div>
              ))}
            </div>
          </section>

          {/* Discount + tax */}
          <section className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <Field label="Discount $">
              <input type="number" min="0" step="0.01" value={discountAmount}
                     onChange={(e) => setDiscountAmount(e.target.value)}
                     data-testid="editor-discount"
                     className="h-11 w-full rounded-xl border border-input bg-card px-3 text-sm tabular-nums" />
            </Field>
            <Field label="Discount Label">
              <input value={discountLabel} onChange={(e) => setDiscountLabel(e.target.value)}
                     placeholder="e.g. Volume, Loyalty"
                     data-testid="editor-discount-label"
                     className="h-11 w-full rounded-xl border border-input bg-card px-3 text-sm" />
            </Field>
          </section>

          {/* Notes */}
          <section className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Notes (visible on invoice)">
              <textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)}
                        data-testid="editor-notes"
                        className="w-full rounded-xl border border-input bg-card px-3 py-2 text-sm" />
            </Field>
            <Field label="Internal Notes (admin + kitchen only)">
              <textarea rows={3} value={internalNotes} onChange={(e) => setInternalNotes(e.target.value)}
                        data-testid="editor-internal-notes"
                        className="w-full rounded-xl border border-input bg-card px-3 py-2 text-sm" />
            </Field>
          </section>

          {/* Live totals — tax removed by launch decision, subtotal-discount = total */}
          <section className="luxury-card p-5 bg-secondary/30">
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm">
              <Total label="Subtotal" value={totals.subtotal} />
              <Total label="Discount" value={-totals.disc} tone={totals.disc > 0 ? "primary" : ""} />
              <Total label="Total" value={totals.total} tone="big" testid="editor-live-total" />
            </div>
          </section>
        </div>

        <div className="p-6 border-t border-border flex items-center justify-between gap-3">
          <button onClick={onClose} disabled={busy}
                  className="h-11 px-4 rounded-xl border border-border bg-card text-sm font-medium">
            Cancel
          </button>
          <button onClick={save} disabled={busy || !order}
                  data-testid="editor-save-btn"
                  className="h-11 px-6 rounded-xl bg-foreground text-background text-sm font-medium hover:bg-foreground/90 disabled:opacity-60">
            {busy ? "Saving…" : "Save Changes"}
          </button>
        </div>
      </div>

      {/* Add-item picker */}
      {showAddPicker && (
        <div className="fixed inset-0 z-[75] bg-foreground/40 backdrop-blur-sm flex items-center justify-center px-4"
             onClick={() => setShowAddPicker(false)}>
          <div onClick={(e) => e.stopPropagation()}
               className="bg-card rounded-2xl border border-border w-full max-w-lg max-h-[80vh] flex flex-col"
               data-testid="editor-item-picker">
            <div className="p-5 border-b border-border">
              <div className="label-eyebrow mb-1">Add Item</div>
              <h3 className="font-display text-lg font-light">From catalog</h3>
              <div className="mt-3 flex gap-2">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <input value={pickerQuery} onChange={(e) => setPickerQuery(e.target.value)}
                         placeholder="Search flavors…"
                         data-testid="editor-picker-search"
                         className="h-10 w-full rounded-lg border border-input bg-card pl-9 pr-3 text-sm" />
                </div>
                <select value={pickerCategory} onChange={(e) => setPickerCategory(e.target.value)}
                        data-testid="editor-picker-category"
                        className="h-10 rounded-lg border border-input bg-card px-2 text-sm">
                  {CATEGORY_ORDER.map((k) => <option key={k} value={k}>{CATEGORY_LABELS[k]}</option>)}
                </select>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto divide-y divide-border">
              {pickerResults.length === 0 && (
                <div className="px-4 py-6 text-center text-sm text-muted-foreground">No matches.</div>
              )}
              {pickerResults.map((c) => (
                <button key={c.id} onClick={() => addFromCatalog(c)}
                        data-testid={`editor-picker-item-${c.id}`}
                        className="w-full text-left px-4 py-3 hover:bg-secondary/50 flex items-center justify-between">
                  <div className="min-w-0">
                    <div className="text-sm font-medium truncate">{c.name}</div>
                    <div className="text-[11px] text-muted-foreground">{c.unit} · ${(c.price ?? 0).toFixed(2)}</div>
                  </div>
                  <Plus className="h-4 w-4 text-muted-foreground shrink-0" />
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="label-eyebrow mb-1.5 block">{label}</span>
      {children}
    </label>
  );
}

function Total({ label, value, tone, testid }) {
  const isBig = tone === "big";
  const isPrimary = tone === "primary";
  return (
    <div>
      <div className={`text-[10px] uppercase tracking-wider text-muted-foreground`}>{label}</div>
      <div
        data-testid={testid}
        className={`tabular-nums ${isBig ? "font-display text-2xl font-light" : "text-sm font-medium"} ${isPrimary ? "text-[hsl(var(--primary))]" : ""}`}
      >
        ${(value || 0).toFixed(2)}
      </div>
    </div>
  );
}
